import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

/** The console's panel, used by every block on this screen. */
export const PANEL = 'rounded-3xl border border-line bg-surface shadow-[0_12px_32px_-8px_rgba(15,23,42,0.12)]';

export interface RailPanelProps {
  icon: LucideIcon;
  title: string;
  /** A link or button in the panel's top-right — "View all". */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * A titled block on the right rail.
 *
 * The three blocks there — what is coming, what the colours mean, and taking
 * the year away with you — are the same object with different contents, so the
 * heading, the icon tile and the padding are written once (CLAUDE.md §3).
 */
export function RailPanel({ icon: Icon, title, action, children, className }: RailPanelProps) {
  return (
    <section className={cn(PANEL, 'flex flex-col gap-4 p-5', className)}>
      <header className="flex items-center gap-2.5">
        <span
          className="relative grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white shadow-sm"
          aria-hidden
        >
          <Icon className="relative h-4 w-4" />
          <span className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-sheen" />
        </span>
        <h2 className="min-w-0 flex-1 truncate text-sm font-black tracking-tight text-ink">
          {title}
        </h2>
        {action}
      </header>
      {children}
    </section>
  );
}
